<script>
  import { onMount, tick } from "svelte";
  import hotspotLogo from "../assets/img/hotspot.png";
  import {
    getAccount,
    getHotspotData,
    loadingStart,
    loadingStop,
  } from "./utils.svelte.js";
  import { getStore } from "./store.svelte.js";
  import Error from "./components/Error.svelte";

  const baseURL = import.meta.env.VITE_BASE_URL;
  const store = getStore();

  // State
  let phoneNumber = null;

  let errorMessage = null;
  let isConnecting = false;
  let hotspotDataPromise = null;

  function refreshHotspotData() {
    if (!hotspotDataPromise) {
      hotspotDataPromise = getHotspotData().finally(() => {
        hotspotDataPromise = null;
      });
    }
    return hotspotDataPromise;
  }

  function clearInput() {
    document.getElementById("phoneNumber").value = "";
    document.getElementById("phoneNumber").focus();
  }

  function urlAccountRedirect() {
    // Redirect to the account view if the URL ends in account/
    const url = new URL(window.location.href);
    if (url.pathname.endsWith("/account/")) {
      store.setState("ACCOUNT");
    }
  }

  // Check if the if phone number and MAC address combination exists
  // If true: log the user in
  async function macLogin(submittedPhone) {
    if (!store.macAddress) {
      return false;
    }
    loadingStart();
    try {
      const response = await fetch(`${baseURL}mac/login/`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          phone_number: submittedPhone,
          mac: store.macAddress || null,
        }),
      });

      if (response.status === 200) {
        return true;
      } else if (response.status === 401 || response.status === 204) {
        return false;
      }
      return null;
    } catch (error) {
      console.error("Error checking MAC status:", error);
      return null;
    } finally {
      loadingStop();
    }
  }

  async function createAccount(submittedPhone) {
    loadingStart();
    try {
      const response = await fetch(`${baseURL}create-account/pin/`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          phone_number: submittedPhone,
          mac: store.macAddress || null,
        }),
      });

      if (response.status === 403) {
        errorMessage = "Phone number is an invalid length";
        clearInput();
      } else if (![200, 204, 206].includes(response.status)) {
        console.error("Failed to create account");
      }
      return response.status;
    } catch (error) {
      console.error("Error creating account:", error);
      return null;
    } finally {
      loadingStop();
    }
  }

  function validatePhoneNumber(phoneNumber) {
    if (phoneNumber === "" || phoneNumber === null) {
      errorMessage = "Invalid phone number";
      return false;
    }

    let phoneNumberCleaned = phoneNumber.replace(/\s/g, "");

    if (phoneNumberCleaned.length !== 10) {
      errorMessage = "Phone number must be 10 characters long";
      return false;
    }

    // Check if the phone number starts with 0
    if (phoneNumberCleaned[0] !== "0") {
      errorMessage = "Phone number must start with 0";
      return false;
    }

    // Check if all characters in the phone number are numeric
    for (let i = 0; i < phoneNumberCleaned.length; i++) {
      const char = phoneNumberCleaned[i];
      if (isNaN(parseInt(char, 10))) {
        errorMessage = "Phone number can only contain numbers";
        return false;
      }
    }

    errorMessage = "";
    return true;
  }

  // Form submission logic
  const handleSubmit = async (event) => {
    event.preventDefault();
    store.setPinLoginNotice(null);

    if (isConnecting) {
      return;
    }

    if (validatePhoneNumber(phoneNumber) === false) {
      return;
    }

    const submittedPhone = phoneNumber;
    isConnecting = true;
    await tick();
    await new Promise((resolve) =>
      requestAnimationFrame(() => requestAnimationFrame(resolve)),
    );

    try {
      store.setCellphone(submittedPhone);
      await refreshHotspotData();

      if (!store.macAddress) {
        const account = await getAccount(submittedPhone);
        if (account?.pin_set) {
          store.setState("PIN_LOGIN");
          return;
        }
      }

      const macLoginOk = await macLogin(submittedPhone);
      if (macLoginOk === true) {
        store.setState("ACCOUNT");
        return;
      }

      // If account exists, go to PIN login
      // If account does not exist, send PIN via SMS to the user and go straight to the app
      const createAccountStatus = await createAccount(submittedPhone);
      if (createAccountStatus === 200 || createAccountStatus === 206) {
        store.setState("ACCOUNT");
        return;
      } else if (createAccountStatus === 204) {
        store.setState("PIN_LOGIN");
        return;
      } else if (createAccountStatus === null) {
        // The request may have reached the API even though the browser lost its
        // response, which is common in iOS captive-portal web views.
        store.setPinLoginNotice(
          "We couldn't confirm the connection. If you received a PIN, enter it below. Otherwise, request a new PIN.",
        );
        store.setState("PIN_LOGIN");
      }
    } finally {
      isConnecting = false;
    }
  };

  onMount(() => {
    urlAccountRedirect();
    refreshHotspotData();

    document.getElementById("phoneNumber")?.focus();
  });
</script>

<div class="h-screen flex flex-col items-center py-6">
  <div class="flex justify-center mt-4 w-full mb-4">
    <img src={hotspotLogo} alt="TooMuchWifi Hotspot" class="w-40" />
  </div>

  <div class="my-8"></div>

  {#if errorMessage}
    <div class="w-80">
      <Error message={errorMessage}></Error>
      <div class="my-8"></div>
    </div>
  {/if}

  <div class="mx-10 my-6">
    <p class="font-medium text-center text-sm">
      Enter your phone number to get started
    </p>
  </div>

  <form on:submit={handleSubmit}>
    <label for="phoneNumber" class="sr-only">Phone number</label>
    <input
      type="tel"
      id="phoneNumber"
      placeholder="Phone Number"
      maxlength="10"
      bind:value={phoneNumber}
      disabled={isConnecting}
      required
      class="border border-gray-300 px-2 py-5 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 w-80"
    />
    <div class="h-8"></div>
    <button
      type="submit"
      disabled={isConnecting}
      class="bg-[#009946] py-5 text-white font-bold rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 w-80 disabled:opacity-80 flex items-center justify-center gap-2"
      aria-busy={isConnecting}
    >
      {#if isConnecting}
        <span
          class="inline-block h-5 w-5 rounded-full border-2 border-white border-t-transparent animate-spin"
          aria-hidden="true"
        ></span>
        <span>Connecting…</span>
      {:else}
        Connect
      {/if}
    </button>
  </form>

  <div class="mb-2 mt-auto w-full">
    <p class="text-center text-xs md:text-sm text-gray-500 w-full">
      By connecting, you agree to our
      <a
        href="https://toomuchwifi.co.za/terms-of-use"
        class="text-blue-500 hover:text-blue-700 underline"
      >
        Terms&nbsp;of&nbsp;Use
      </a>
      and
      <a
        href="https://toomuchwifi.co.za/privacy-policy"
        class="text-blue-500 hover:text-blue-700 underline"
      >
        Privacy&nbsp;Policy
      </a>.
    </p>
  </div>
</div>
